import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { FilmScreenshot } from "../../types/entities/filmDetail"

const MONO = "'JetBrains Mono', monospace"
const BODY = "'Manrope', sans-serif"

const btnClass =
    "flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white/25 bg-black/60 text-white outline-none transition hover:bg-black/80"

function NavButton({ direction, onClick }: { direction: "prev" | "next"; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={direction === "prev" ? "Предыдущий кадр" : "Следующий кадр"}
            className={btnClass}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <path
                    d={direction === "prev" ? "M11 4L6 9l5 5" : "M7 4l5 5-5 5"}
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    )
}

export default function FilmScreenshots({ screenshots }: { screenshots: FilmScreenshot[] }) {
    const scrollerRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<{ startX: number; startScroll: number; moved: boolean } | null>(null)
    const skipClickRef = useRef(false)
    const skipClickTimerRef = useRef<number | null>(null)
    const swipeRef = useRef<number | null>(null)
    const [activeIndex, setActiveIndex] = useState<number | null>(null)

    const DRAG_THRESHOLD_PX = 10

    const multi = screenshots.length > 1
    const open = activeIndex !== null
    const current = activeIndex !== null ? screenshots[activeIndex] : null

    useEffect(() => {
        const el = scrollerRef.current
        if (!el) return

        const onWheel = (e: WheelEvent) => {
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
            if (el.scrollWidth <= el.clientWidth) return
            e.preventDefault()
            el.scrollLeft += e.deltaY
        }

        el.addEventListener("wheel", onWheel, { passive: false })
        return () => el.removeEventListener("wheel", onWheel)
    }, [screenshots.length])

    useEffect(() => {
        return () => {
            if (skipClickTimerRef.current !== null) window.clearTimeout(skipClickTimerRef.current)
        }
    }, [])

    useEffect(() => {
        if (!open) return

        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = "hidden"

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setActiveIndex(null)
            if (!multi) return
            if (e.key === "ArrowLeft") {
                setActiveIndex(i => (i === null ? i : (i - 1 + screenshots.length) % screenshots.length))
            }
            if (e.key === "ArrowRight") {
                setActiveIndex(i => (i === null ? i : (i + 1) % screenshots.length))
            }
        }

        window.addEventListener("keydown", onKey)
        return () => {
            document.body.style.overflow = prevOverflow
            window.removeEventListener("keydown", onKey)
        }
    }, [open, multi, screenshots.length])

    if (screenshots.length === 0) return null

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        const el = scrollerRef.current
        if (!el || e.pointerType === "touch") return
        if (skipClickTimerRef.current !== null) {
            window.clearTimeout(skipClickTimerRef.current)
            skipClickTimerRef.current = null
        }
        skipClickRef.current = false
        dragRef.current = { startX: e.clientX, startScroll: el.scrollLeft, moved: false }
    }

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const el = scrollerRef.current
        const drag = dragRef.current
        if (!el || !drag) return
        const dx = e.clientX - drag.startX
        if (!drag.moved) {
            if (Math.abs(dx) < DRAG_THRESHOLD_PX) return
            drag.moved = true
            el.setPointerCapture(e.pointerId)
        }
        el.scrollLeft = drag.startScroll - dx
    }

    const onPointerUp = () => {
        if (dragRef.current?.moved) {
            skipClickRef.current = true
            // Если click после drag не пришёл — не оставляем флаг на следующий клик
            if (skipClickTimerRef.current !== null) window.clearTimeout(skipClickTimerRef.current)
            skipClickTimerRef.current = window.setTimeout(() => {
                skipClickRef.current = false
                skipClickTimerRef.current = null
            }, 120)
        }
        dragRef.current = null
    }

    const openAt = (index: number) => {
        if (skipClickRef.current) {
            skipClickRef.current = false
            if (skipClickTimerRef.current !== null) {
                window.clearTimeout(skipClickTimerRef.current)
                skipClickTimerRef.current = null
            }
            return
        }
        setActiveIndex(index)
    }

    const goPrev = () => {
        setActiveIndex(i => (i === null ? i : (i - 1 + screenshots.length) % screenshots.length))
    }

    const goNext = () => {
        setActiveIndex(i => (i === null ? i : (i + 1) % screenshots.length))
    }

    return (
        <section className="min-w-0 overflow-hidden rounded-[24px] border border-white/10 bg-white/[0.04] p-4 md:p-5 lg:flex-1 lg:self-start">
            <h2
                className="mb-3 shrink-0 text-white/35 text-[10px] uppercase tracking-[0.1em]"
                style={{ fontFamily: MONO, fontWeight: 700 }}>
                Кадры из фильма
            </h2>

            <div
                ref={scrollerRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                className="@container -mx-4 cursor-grab overflow-x-auto overscroll-x-contain pb-1 scrollbar-hide active:cursor-grabbing md:-mx-5">
                <div className="flex snap-x snap-mandatory gap-2 pl-4 md:pl-5">
                    {screenshots.map((shot, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => openAt(i)}
                            className={`flex h-[220px] shrink-0 snap-start cursor-zoom-in items-center border-0 bg-transparent p-0 sm:h-[280px] md:h-[320px] lg:h-[400px] ${
                                multi ? "max-w-[calc(100cqw-3rem)]" : ""
                            }`}>
                            <img
                                src={shot.big}
                                alt=""
                                draggable={false}
                                className="pointer-events-none block h-full w-auto max-w-full rounded-[16px] object-contain"
                            />
                        </button>
                    ))}
                    {multi && <div className="w-4 shrink-0 md:w-5" aria-hidden />}
                </div>
            </div>

            {open &&
                current &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[220] flex flex-col bg-black/92"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Просмотр кадра"
                        onClick={() => setActiveIndex(null)}>
                        <div
                            className="flex shrink-0 items-center justify-end px-3 pt-[max(0.75rem,var(--safe-top))] pb-2 md:px-5"
                            onClick={e => e.stopPropagation()}>
                            <button
                                type="button"
                                onClick={() => setActiveIndex(null)}
                                aria-label="Закрыть"
                                className={btnClass}>
                                <svg width="12" height="12" viewBox="0 0 10 10" fill="none" aria-hidden>
                                    <path
                                        d="M1 1l8 8M9 1L1 9"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div
                            className="flex min-h-0 flex-1 items-center gap-1 px-2 md:gap-3 md:px-5"
                            onClick={e => e.stopPropagation()}>
                            {multi ? <NavButton direction="prev" onClick={goPrev} /> : <div className="size-11 shrink-0" />}

                            <img
                                src={current.big}
                                alt=""
                                className="mx-auto max-h-full min-w-0 flex-1 object-contain select-none"
                                draggable={false}
                                onTouchStart={e => {
                                    swipeRef.current = e.changedTouches[0]?.clientX ?? null
                                }}
                                onTouchEnd={e => {
                                    if (!multi || swipeRef.current === null) return
                                    const endX = e.changedTouches[0]?.clientX
                                    if (endX === undefined) return
                                    const dx = endX - swipeRef.current
                                    swipeRef.current = null
                                    if (Math.abs(dx) < 48) return
                                    if (dx > 0) goPrev()
                                    else goNext()
                                }}
                            />

                            {multi ? <NavButton direction="next" onClick={goNext} /> : <div className="size-11 shrink-0" />}
                        </div>

                        <div className="flex shrink-0 items-center justify-center px-3 pt-2 pb-[max(0.75rem,var(--safe-bottom))]">
                            {multi ? (
                                <div
                                    className="rounded-full bg-white/10 px-3 py-1 text-[12px] text-white/80"
                                    style={{ fontFamily: BODY, fontWeight: 600 }}>
                                    {(activeIndex ?? 0) + 1} / {screenshots.length}
                                </div>
                            ) : (
                                <div className="h-7" />
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </section>
    )
}
